import React, { useState, useEffect, useRef } from 'react';
import { Brain, Zap, Target, Users, ChevronRight, Globe } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const translations = {
    en: {
        nav: {
            home: 'Home',
            about: 'About',
            games: 'Games'
        },
        hero: {
            title: 'Train Your Brain, Master Your Mind',
            subtitle: 'Boost your cognitive abilities with scientifically-designed brain games that challenge and improve your mental performance.',
            cta: 'Start Training Now',
            ctaSecondary: 'Learn More'
        },
        features: {
            title: 'Why Choose Brain4Everyone?',
            subtitle: 'Our platform combines neuroscience research with engaging gameplay to deliver real cognitive improvements.',
            items: [
                {
                    icon: Brain,
                    title: 'Scientifically Proven',
                    description: 'Games based on cognitive science research to ensure real brain improvements.'
                },
                {
                    icon: Zap,
                    title: 'Adaptive Training',
                    description: 'AI-powered difficulty adjustment that challenges you at the perfect level.'
                },
                {
                    icon: Target,
                    title: 'Focused Goals',
                    description: 'Target specific cognitive skills like memory, attention, and problem-solving.'
                },
                {
                    icon: Users,
                    title: 'Community Driven',
                    description: 'Join thousands of users on their journey to better cognitive health.'
                }
            ]
        },
        games: {
            title: 'Brain Training Games',
            subtitle: 'Discover our collection of engaging games designed to enhance different aspects of your cognitive function.',
            categories: [
                {
                    title: 'Memory Games',
                    description: 'Strengthen your working memory and recall abilities.',
                    games: ['Memory Matrix', 'Pattern Recognition', 'Sequence Master']
                },
                {
                    title: 'Attention Training',
                    description: 'Improve focus and concentration through targeted exercises.',
                    games: ['Focus Flow', 'Distraction Filter', 'Attention Span']
                },
                {
                    title: 'Problem Solving',
                    description: 'Enhance logical thinking and analytical skills.',
                    games: ['Logic Puzzles', 'Pattern Analysis', 'Strategic Thinking']
                }
            ],
            cta: 'Explore All Games'
        },
        cta: {
            title: 'Ready to Transform Your Mind?',
            subtitle: 'Join thousands of users who have already started their cognitive improvement journey.',
            button: 'Start Your Journey',
            stats: [
                { number: '50K+', label: 'Active Users' },
                { number: '100+', label: 'Brain Games' },
                { number: '95%', label: 'User Satisfaction' }
            ]
        },
        service: {
            title: 'Service Information',
            description: 'Enjoy the service by sending ACCESS to 8900. The Mind-Fit service includes cognitive training, mindfulness sessions and community support for a holistic approach to mental well-being. You will have access to 50 online games, unlimited for 3 months. The cost of accessing the service is €3.50 gross (VAT included). For support, contact support@allopass.com.',
            sms: 'Send ACCESS to 8900',
            price: '€3.50 gross (VAT included)',
            support: 'support@allopass.com'
        }
    },
    fr: {
        nav: {
            home: 'Accueil',
            about: 'À Propos',
            games: 'Jeux'
        },
        hero: {
            title: 'Entraînez Votre Cerveau, Maîtrisez Votre Esprit',
            subtitle: 'Améliorez vos capacités cognitives avec des jeux cérébraux conçus scientifiquement qui défient et améliorent vos performances mentales.',
            cta: 'Commencer l\'Entraînement',
            ctaSecondary: 'En Savoir Plus'
        },
        features: {
            title: 'Pourquoi Choisir Brain4Everyone?',
            subtitle: 'Notre plateforme combine la recherche en neurosciences avec un gameplay engageant pour offrir de vraies améliorations cognitives.',
            items: [
                {
                    icon: Brain,
                    title: 'Scientifiquement Prouvé',
                    description: 'Jeux basés sur la recherche en sciences cognitives pour garantir de vraies améliorations cérébrales.'
                },
                {
                    icon: Zap,
                    title: 'Entraînement Adaptatif',
                    description: 'Ajustement de difficulté alimenté par IA qui vous défie au niveau parfait.'
                },
                {
                    icon: Target,
                    title: 'Objectifs Ciblés',
                    description: 'Ciblez des compétences cognitives spécifiques comme la mémoire, l\'attention et la résolution de problèmes.'
                },
                {
                    icon: Users,
                    title: 'Communauté',
                    description: 'Rejoignez des milliers d\'utilisateurs dans leur parcours vers une meilleure santé cognitive.'
                }
            ]
        },
        games: {
            title: 'Jeux d\'Entraînement Cérébral',
            subtitle: 'Découvrez notre collection de jeux engageants conçus pour améliorer différents aspects de votre fonction cognitive.',
            categories: [
                {
                    title: 'Jeux de Mémoire',
                    description: 'Renforcez votre mémoire de travail et vos capacités de rappel.',
                    games: ['Matrice de Mémoire', 'Reconnaissance de Motifs', 'Maître des Séquences']
                },
                {
                    title: 'Entraînement de l\'Attention',
                    description: 'Améliorez la concentration et l\'attention grâce à des exercices ciblés.',
                    games: ['Flux de Concentration', 'Filtre de Distraction', 'Durée d\'Attention']
                },
                {
                    title: 'Résolution de Problèmes',
                    description: 'Améliorez la pensée logique et les compétences analytiques.',
                    games: ['Puzzles Logiques', 'Analyse de Motifs', 'Pensée Stratégique']
                }
            ],
            cta: 'Explorer Tous les Jeux'
        },
        cta: {
            title: 'Prêt à Transformer Votre Esprit?',
            subtitle: 'Rejoignez des milliers d\'utilisateurs qui ont déjà commencé leur parcours d\'amélioration cognitive.',
            button: 'Commencez Votre Parcours',
            stats: [
                { number: '50K+', label: 'Utilisateurs Actifs' },
                { number: '100+', label: 'Jeux Cérébraux' },
                { number: '95%', label: 'Satisfaction Utilisateur' }
            ]
        },
        service: {
            title: 'Informations sur le Service',
            description: 'Profitez du service en envoyant ACCESS au 8900. Le service Mind-Fit comprend un entraînement cognitif, avec des séances de pleine conscience et un soutien communautaire pour une approche holistique du bien-être mental. Vous disposerez de 50 jeux en ligne, de façon illimitée pendant 3 mois. Le coût de l\'accès au service est de 3,5 € bruts (TVA incluse). Pour le support, joignez support@allopass.com.',
            sms: 'Envoyez ACCESS au 8900',
            price: '3,5 € bruts (TVA incluse)',
            support: 'support@allopass.com'
        }
    }
};

function LanguageSwitcher({ currentLang, onLanguageChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const languages = [
        { code: 'en', name: 'English', flag: 'US' },
        { code: 'fr', name: 'Français', flag: 'FR' }
    ];

    const currentLanguage = languages.find(lang => lang.code === currentLang);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
                <ReactCountryFlag
                    countryCode={currentLanguage.flag}
                    svg
                    style={{
                        width: '20px',
                        height: '15px'
                    }}
                />
                <span className="text-sm font-medium">{currentLanguage.name}</span>
                <svg
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                        {languages.map((language) => (
                            <button
                                key={language.code}
                                onClick={() => {
                                    onLanguageChange(language.code);
                                    setIsOpen(false);
                                }}
                                className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${currentLang === language.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                                    }`}
                            >
                                <ReactCountryFlag
                                    countryCode={language.flag}
                                    svg
                                    style={{
                                        width: '20px',
                                        height: '15px'
                                    }}
                                />
                                <span className="font-medium">{language.name}</span>
                                {currentLang === language.code && (
                                    <svg className="w-4 h-4 ml-auto text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function App() {
    const [language, setLanguage] = useState('en');
    const t = translations[language];

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="flex items-center space-x-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                                        <Brain className="w-5 h-5 text-white" />
                                    </div>
                                    <span className="text-xl font-bold text-gray-900">Brain4Everyone</span>
                                </div>
                            </div>
                        </div>

                        <div className="hidden md:block">
                            <div className="ml-10 flex items-baseline space-x-8">
                                <a href="#home" className="text-gray-900 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
                                    {t.nav.home}
                                </a>
                                <a href="#about" className="text-gray-500 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
                                    {t.nav.about}
                                </a>
                                <a href="#games" className="text-gray-500 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
                                    {t.nav.games}
                                </a>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <LanguageSwitcher currentLang={language} onLanguageChange={setLanguage} />
                            <a
                                href="/s"
                                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                            >
                                {t.hero.cta}
                            </a>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section id="home" className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
                <div className="absolute inset-0 neural-pattern opacity-30"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8">
                            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                                {t.hero.title}
                            </h1>
                            <p className="text-xl text-gray-600 leading-relaxed">
                                {t.hero.subtitle}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <a
                                    href="/s"
                                    className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors flex items-center justify-center group"
                                >
                                    {t.hero.cta}
                                    <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </a>
                                <a
                                    href="#about"
                                    className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors"
                                >
                                    {t.hero.ctaSecondary}
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="relative z-10">
                                <div className="w-full h-96 rounded-2xl shadow-2xl overflow-hidden">
                                    <img
                                        src="/static/images/hero.jpeg"
                                        alt="Brain training visualization"
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center">
                                        <Brain className="w-32 h-32 text-white animate-pulse-slow drop-shadow-lg" />
                                    </div>
                                </div>
                            </div>
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-400 rounded-full opacity-20 animate-float"></div>
                            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-pink-400 rounded-full opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="about" className="py-20 bg-white relative">
                <div className="absolute inset-0 opacity-5">
                    <img
                        src="/static/images/achievement.jpeg"
                        alt="Achievement background"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                            {t.features.title}
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            {t.features.subtitle}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {t.features.items.map((item, index) => (
                            <div key={index} className="text-center group">
                                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                                    <item.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                    {item.title}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Games Section */}
            <section id="games" className="py-20 bg-gray-50 relative">
                <div className="absolute inset-0 opacity-10">
                    <img
                        src="/static/images/game.png"
                        alt="Brain games background"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                            {t.games.title}
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            {t.games.subtitle}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                        {t.games.categories.map((category, index) => (
                            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                    {category.title}
                                </h3>
                                <p className="text-gray-600 mb-6">
                                    {category.description}
                                </p>
                                <ul className="space-y-2">
                                    {category.games.map((game, gameIndex) => (
                                        <li key={gameIndex} className="flex items-center text-gray-700">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                                            {game}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <a
                            href="/s"
                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center group"
                        >
                            {t.games.cta}
                            <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </a>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img
                        src="/static/images/success.jpeg"
                        alt="Success and achievement background"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                        {t.cta.title}
                    </h2>
                    <p className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto">
                        {t.cta.subtitle}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                        {t.cta.stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-4xl font-bold text-white mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-blue-100">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    <a
                        href="/s"
                        className="bg-white text-blue-600 px-12 py-4 rounded-lg font-bold text-xl hover:bg-gray-100 transition-colors inline-flex items-center group"
                    >
                        {t.cta.button}
                        <ChevronRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="col-span-1 md:col-span-2">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                                    <Brain className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-xl font-bold">Brain4Everyone</span>
                            </div>
                            <p className="text-gray-400 mb-4">
                                Transform your cognitive abilities with scientifically-designed brain training challenges.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-4">Quick Links</h3>
                            <ul className="space-y-2">
                                <li><a href="#home" className="text-gray-400 hover:text-white transition-colors">Home</a></li>
                                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors">About</a></li>
                                <li><a href="#games" className="text-gray-400 hover:text-white transition-colors">Games</a></li>
                                <li><a href="/s" className="text-gray-400 hover:text-white transition-colors">Start Training</a></li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-4">Support</h3>
                            <ul className="space-y-2">
                                <li><a href="/privacy.html" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a></li>
                                <li><a href="/terms.html" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a></li>
                            </ul>
                        </div>
                    </div>

                    {/* Service Information Section */}
                    <div className="border-t border-gray-800 mt-8 pt-8">
                        <div className="bg-gray-800 rounded-lg p-6 mb-6">
                            <h3 className="text-lg font-semibold mb-4 text-center">
                                {t.service.title}
                            </h3>
                            <p className="text-gray-300 text-sm leading-relaxed mb-4">
                                {t.service.description}
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm">
                                <div className="flex items-center space-x-2">
                                    <span className="text-gray-400">SMS:</span>
                                    <span className="font-mono bg-gray-700 px-2 py-1 rounded">{t.service.sms}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="text-gray-400">Price:</span>
                                    <span className="font-semibold text-green-400">{t.service.price}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="text-gray-400">Support:</span>
                                    <a href={`mailto:${t.service.support}`} className="text-blue-400 hover:text-blue-300 transition-colors">
                                        {t.service.support}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
                        <p>&copy; 2024 Brain4Everyone. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default App;
